/**
 * Where the editor gets its schemas.
 *
 * TWO SOURCES, ONE SHAPE. In development and in the Playwright harness the schemas come
 * straight off disk from `docs/contract/schema/` - served, not copied, by
 * `vite-plugin-contract-schema`. In production they come from the backend sidecar's artifact
 * route, content-hashed and revalidated.
 *
 * THE SECOND SOURCE IS A DEPLOYMENT STEP, NOT A CODE ONE. The sidecar serves the routes and the
 * base below points at them; what remains is filling the sidecar's `ARTIFACT_DIR` with
 * `docs/contract/schema/` (as `schema/`) and the mod's own `web/` output. A deployment that skips
 * it gets a refusal whose `detail` names the variable and the directory, and the editor prints that
 * sentence (`sidecarDetail`) rather than showing a silent empty form. See `web/deploy/RUNBOOK.md`,
 * step 10.
 *
 * Serving the contract directory rather than copying it into `public/` is deliberate: a copy
 * is a second place the schemas exist, it goes stale, and it would have to be excluded from
 * the "no schema was hand-edited" gate. There is no copy, so the gate stays a plain
 * `git diff` over one directory and the dev editor is always looking at exactly what the
 * generator wrote.
 *
 * The base is one knob, `VITE_SCHEMA_BASE`, and it genuinely turns something - see
 * `resolveSchemaBase` and its tests. A Pages build sets it to the backend prefix; leaving it
 * unset yields the contract mirror, which is correct for dev and for `vite preview` and wrong
 * for a deployment, where the route simply does not exist and the editor says so out loud.
 */
import { sidecarDetail } from '../api/sidecarDetail';
import type { SchemaNode } from '../blocks/types';

/** Served by the Vite plugin from the repo's committed contract mirror. */
export const CONTRACT_BASE = '/schema/';

/**
 * The sidecar's versioned-artifact route. See the header for what a deployment still has to put
 * behind it.
 */
export const BACKEND_BASE = '/v1/artifacts/schema/';

export interface SchemaSummary {
  /** File stem, e.g. `reward-definition`. The editor route parameter. */
  readonly id: string;
  /** The schema's `title`, e.g. `RewardDefinition`. */
  readonly title: string;
  /** `x-mod-version` - which build of the mod produced this contract. */
  readonly modVersion: string;
}

export interface SchemaIndex {
  readonly source: 'contract' | 'backend';
  readonly schemas: readonly SchemaSummary[];
}

/**
 * Picks the base URL. Exported and tested rather than inlined, because "the knob that turned
 * nothing" is a mistake this repo has already paid for once.
 */
export function resolveSchemaBase(env: Readonly<Record<string, string | boolean | undefined>>): string {
  const configured = env['VITE_SCHEMA_BASE'];
  if (typeof configured === 'string' && configured !== '') {
    return configured.endsWith('/') ? configured : `${configured}/`;
  }
  return CONTRACT_BASE;
}

export function sourceKindOf(base: string): SchemaIndex['source'] {
  return base === CONTRACT_BASE ? 'contract' : 'backend';
}

export class SchemaFetchError extends Error {
  constructor(
    readonly url: string,
    readonly status: number | null,
    message: string,
  ) {
    super(message);
    this.name = 'SchemaFetchError';
  }
}

export interface SchemaSourceOptions {
  readonly base?: string;
  readonly fetchImpl?: typeof fetch;
}

/**
 * Reads the schema index and individual schemas.
 *
 * Both methods throw `SchemaFetchError` with a message written for the person who will read
 * it in the UI - "the contract mirror is not being served" is actionable, "Failed to fetch"
 * is not.
 */
export class SchemaSource {
  readonly base: string;
  private readonly fetchImpl: typeof fetch;

  constructor(options: SchemaSourceOptions = {}) {
    this.base = options.base ?? CONTRACT_BASE;
    this.fetchImpl = options.fetchImpl ?? globalThis.fetch.bind(globalThis);
  }

  get kind(): SchemaIndex['source'] {
    return sourceKindOf(this.base);
  }

  url(id: string): string {
    return `${this.base}${id}.json`;
  }

  async index(): Promise<SchemaIndex> {
    const url = `${this.base}index.json`;
    const body = await this.read(url, 'the list of editable configs');
    const schemas = Array.isArray((body as { schemas?: unknown }).schemas) ? ((body as { schemas: SchemaSummary[] }).schemas) : [];
    return { source: this.kind, schemas };
  }

  async schema(id: string): Promise<SchemaNode> {
    return (await this.read(this.url(id), `the schema for "${id}"`)) as SchemaNode;
  }

  private async read(url: string, what: string): Promise<unknown> {
    let response: Response;
    try {
      response = await this.fetchImpl(url, { headers: { accept: 'application/json' } });
    } catch (cause) {
      throw new SchemaFetchError(url, null, `Could not reach ${url} to load ${what}. ${hint(this.kind)}`);
    }
    if (!response.ok) {
      // The sidecar's own sentence when it wrote one: it names the directory on that machine and
      // what to copy into it. Otherwise (a proxy's error page, an empty body) our general hint.
      const said = await sidecarDetail(response);
      throw new SchemaFetchError(
        url,
        response.status,
        said === null ? `${url} answered ${response.status} for ${what}. ${hint(this.kind)}` : `${url} answered ${response.status} for ${what}: ${said}`,
      );
    }
    try {
      return await response.json();
    } catch {
      throw new SchemaFetchError(url, response.status, `${url} did not answer with JSON for ${what}.`);
    }
  }
}

/**
 * The schema documents a session envelope binds, keyed by the `schemaId` the binding names.
 *
 * TWO NAMES FOR ONE SCHEMA, and this is the function that reconciles them. A
 * `SessionSchemaBinding` names a schema the way the MOD knows it - `KitDefinition`, the POJO's
 * own name - while the artifact route and the contract mirror are keyed by FILE STEM,
 * `kit-definition`, because that is what `:schema-gen` writes and what the sidecar's path
 * pattern admits. The index is the only document that carries both, so it is read first and
 * the mapping comes out of the data rather than out of a guessed kebab-casing here.
 *
 * EVERY FAILURE DEGRADES, NONE THROWS. A schema that will not load simply is not in the map,
 * and the reference walk then checks that file's `{kind, name}` references and says so
 * (`RefGraph.schemaChecked`). An editor that refused to show a bundle because one schema
 * 404'd would be trading a weaker check for no editor.
 */
export async function loadBoundSchemas(source: SchemaSource, ids: readonly string[]): Promise<ReadonlyMap<string, SchemaNode>> {
  const wanted = [...new Set(ids)].filter((id) => id !== '');
  const loaded = new Map<string, SchemaNode>();
  if (wanted.length === 0) return loaded;

  const stems = new Map<string, string>();
  try {
    for (const summary of (await source.index()).schemas) {
      stems.set(summary.id, summary.id);
      if (summary.title !== '') stems.set(summary.title, summary.id);
    }
  } catch {
    // No index: fall through and try each id as if it were already a stem. Costs one 404.
  }

  await Promise.all(
    wanted.map(async (id) => {
      try {
        loaded.set(id, await source.schema(stems.get(id) ?? id));
      } catch {
        /* This file gets the structural walk only; see the header. */
      }
    }),
  );
  return loaded;
}

function hint(kind: SchemaIndex['source']): string {
  return kind === 'contract'
    ? 'In development the schemas are served from docs/contract/schema by vite-plugin-contract-schema; run the app through Vite rather than a static file server.'
    : 'The backend sidecar serves the schemas from the schema/ directory of its ARTIFACT_DIR: check that the sidecar is up, that ARTIFACT_DIR is set, and that a copy of docs/contract/schema is in it.';
}
